import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'

interface Prize {
  label: string
  value: string
  type: 'fixed' | 'percent'
  weight: number
  color: string
}

interface CampaignConfig {
  id: number
  name: string
  heading: string
  subheading?: string
  is_enabled: boolean
  code_validity_days: number
  prizes: Prize[]
  already_spun: boolean
}

interface SpinResult {
  prize_label: string
  prize_value: string
  prize_type: string
  discount_code: string
  expires_at: string
  validity_days: number
}

interface RecentWin {
  first_name: string
  prize_label: string
  spun_at: string
}

// ── Canvas ────────────────────────────────────────────────────────────────────

function segmentAngles(prizes: Prize[]) {
  const total = prizes.reduce((s, p) => s + (p.weight || 1), 0)
  let start = 0
  return prizes.map(p => {
    const sweep = ((p.weight || 1) / total) * Math.PI * 2
    const obj = { start, sweep }
    start += sweep
    return obj
  })
}

function drawWheel(canvas: HTMLCanvasElement, prizes: Prize[], offsetAngle: number) {
  const ctx = canvas.getContext('2d')!
  const dpr = window.devicePixelRatio || 1
  const size = canvas.width / dpr
  const cx = size / 2
  const cy = size / 2
  const radius = cx - 5

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  const segs = segmentAngles(prizes)

  segs.forEach((seg, i) => {
    const p = prizes[i]
    const start = seg.start + offsetAngle
    const end = start + seg.sweep
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.arc(cx, cy, radius, start, end)
    ctx.closePath()
    ctx.fillStyle = p.color || '#166534'
    ctx.fill()
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'
    ctx.lineWidth = 1.5
    ctx.stroke()

    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate(start + seg.sweep / 2)
    ctx.textAlign = 'right'
    ctx.fillStyle = '#ffffff'
    ctx.font = `bold ${size > 300 ? 14 : 12}px Calibri, Arial, sans-serif`
    ctx.shadowColor = 'rgba(0,0,0,0.7)'
    ctx.shadowBlur = 4
    ctx.fillText(p.label, radius - 12, 5)
    ctx.restore()
  })

  // Outer ring — green accent
  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.strokeStyle = 'rgba(64,163,69,0.7)'
  ctx.lineWidth = 4
  ctx.stroke()

  // White hub
  ctx.beginPath()
  ctx.arc(cx, cy, 34, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  ctx.strokeStyle = 'rgba(64,163,69,0.6)'
  ctx.lineWidth = 2.5
  ctx.stroke()
}

function weightedPick(prizes: Prize[]) {
  const total = prizes.reduce((s, p) => s + (p.weight || 1), 0)
  let r = Math.random() * total
  for (let i = 0; i < prizes.length; i++) {
    r -= prizes[i].weight || 1
    if (r <= 0) return i
  }
  return prizes.length - 1
}

function easeOut(t: number) { return 1 - Math.pow(1 - t, 3.5) }

// ── Confetti ──────────────────────────────────────────────────────────────────

function launchConfetti() {
  const container = document.getElementById('sw-confetti')
  if (!container) return
  container.innerHTML = ''
  const colors = ['#40A345', '#86efac', '#059669', '#4ade80', '#fbbf24', '#d1fae5', '#ffffff']
  for (let i = 0; i < 80; i++) {
    const el = document.createElement('div')
    const color = colors[Math.floor(Math.random() * colors.length)]
    const size = 6 + Math.random() * 8
    const xDrift = (Math.random() - 0.5) * 120
    el.style.cssText = `
      position:absolute;left:${Math.random() * 100}%;top:-20px;
      width:${size}px;height:${size * (0.4 + Math.random() * 0.8)}px;
      background:${color};border-radius:${Math.random() > 0.5 ? '50%' : '2px'};
      animation:swFall ${2 + Math.random() * 2}s ease-in ${Math.random() * 0.6}s forwards;
      transform:rotate(${Math.random() * 360}deg);--x:${xDrift}px;
    `
    container.appendChild(el)
  }
  if (!document.getElementById('sw-confetti-style')) {
    const s = document.createElement('style')
    s.id = 'sw-confetti-style'
    s.textContent = `@keyframes swFall{to{top:110vh;transform:rotate(720deg) translateX(var(--x));opacity:0;}}`
    document.head.appendChild(s)
  }
  setTimeout(() => { container.innerHTML = '' }, 4000)
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SpinWheel() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isGuestMode = !user

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const angleRef = useRef(0)
  const animRef = useRef<number>()

  const [config, setConfig] = useState<CampaignConfig | null>(null)
  const [publicConfig, setPublicConfig] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [spinning, setSpinning] = useState(false)
  const [result, setResult] = useState<SpinResult | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [copied, setCopied] = useState(false)
  const [recentWins, setRecentWins] = useState<RecentWin[]>([])

  const prizes: Prize[] = config?.prizes || publicConfig?.prizes || []

  function getCanvasSize() {
    const vw = window.innerWidth
    if (vw < 380) return 260
    if (vw < 640) return 295
    if (vw < 1024) return 330
    return 345
  }

  const setupCanvas = useCallback((p: Prize[]) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const size = getCanvasSize()
    canvas.style.width = `${size}px`
    canvas.style.height = `${size}px`
    canvas.width = size * dpr
    canvas.height = size * dpr
    canvas.getContext('2d')!.scale(dpr, dpr)
    drawWheel(canvas, p, angleRef.current)
  }, [])

  useEffect(() => {
    async function load() {
      try {
        if (user) {
          const data: CampaignConfig = await api.get('/spin-wheel/config').then(r => r.data)
          setConfig(data)
          if (data.already_spun) { navigate('/account', { replace: true }); return }
        } else {
          const pub = await api.get('/spin-wheel/public').then(r => r.data)
          if (!pub.visible) { navigate('/', { replace: true }); return }
          setPublicConfig(pub)
        }
      } catch { setError('This promotion is not currently active.') }
      finally { setLoading(false) }
    }
    load()
  }, [user, navigate])

  useEffect(() => {
    api.get('/spin-wheel/recent-wins').then(r => setRecentWins(r.data)).catch(() => {})
  }, [])

  useEffect(() => { if (prizes.length > 0) setupCanvas(prizes) }, [prizes, setupCanvas])

  useEffect(() => {
    const onResize = () => { if (prizes.length > 0) setupCanvas(prizes) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [prizes, setupCanvas])

  async function handleSpin() {
    if (!config || spinning || isGuestMode) return
    setSpinning(true)
    const p = config.prizes
    const winIdx = weightedPick(p)
    const segs = segmentAngles(p)
    const winSeg = segs[winIdx]
    const pointerAngle = -Math.PI / 2
    const segMid = winSeg.start + winSeg.sweep / 2
    const targetOffset = pointerAngle - segMid
    const normalised = ((targetOffset % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
    const targetAngle = normalised + (5 + Math.floor(Math.random() * 3)) * Math.PI * 2
    const startAngle = angleRef.current
    const duration = 4200 + Math.random() * 800
    let startTime: number | null = null

    function animate(ts: number) {
      if (!startTime) startTime = ts
      const progress = Math.min((ts - startTime) / duration, 1)
      const current = startAngle + targetAngle * easeOut(progress)
      angleRef.current = current
      drawWheel(canvasRef.current!, p, current)
      if (progress < 1) { animRef.current = requestAnimationFrame(animate) }
      else {
        angleRef.current = ((startAngle + targetAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2)
        submitSpin()
      }
    }
    animRef.current = requestAnimationFrame(animate)
  }

  async function submitSpin() {
    if (!config) return
    try {
      const res = await api.post('/spin-wheel/spin', { campaign_id: config.id, prize_index: 0 })
      setResult(res.data)
      setShowResult(true)
      launchConfetti()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.')
    } finally { setSpinning(false) }
  }

  function copyCode() {
    if (!result) return
    navigator.clipboard.writeText(result.discount_code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  const tickerItems = recentWins.length > 0
    ? recentWins.map(w => `🎉 ${w.first_name} just won ${w.prize_label}!`)
    : ['🎉 Emma just won 10% Off!', '🚀 James saved 8%!', '🎊 Sarah won 15% Off!',
       '⚡ David won 5% Off!', '🎉 Priya just won 12% Off!', '🚀 Tom saved 10%!']
  const tickerText = [...tickerItems, ...tickerItems].join('    ✦    ')

  const campaignName = config?.name || publicConfig?.name || 'Spin & Win'
  const heading = config?.heading || publicConfig?.heading || 'Spin to Save!'
  const validityDays = config?.code_validity_days || publicConfig?.code_validity_days || 14

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
      <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] bg-gray-50 p-8 text-center">
      <div className="text-5xl mb-4">🎡</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Promotion Not Active</h2>
      <p className="text-gray-500 mb-6">{error}</p>
      <Link to="/shop" className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-bold rounded-xl hover:shadow-lg transition-all hover:-translate-y-0.5">
        Browse Our Plans
      </Link>
    </div>
  )

  const tcItems = [
    { icon: '✅', bold: 'New eligible product orders only.', rest: ' Brand-new SIM, broadband, or bundle orders only.' },
    { icon: '📋', bold: 'Eligible products only.', rest: " Not all products participate. You'll be told at checkout if yours is excluded." },
    { icon: '📆', bold: 'Contract discount applies monthly.', rest: ' On a contract, the discount reduces your monthly fee for the full term.' },
    { icon: '❌', bold: 'Not valid for top-ups', rest: ' or any account top-up payments.' },
    { icon: '❌', bold: 'Not valid for account payments.', rest: ' Cannot be used to pay a bill or existing contract in My Account.' },
    { icon: '❌', bold: 'Not valid on existing orders,', rest: ' renewals, upgrades, or plan changes.' },
    { icon: '🔒', bold: 'One spin, one use per customer.', rest: ' Cannot be reused or transferred.' },
    { icon: '📅', bold: `Expires in ${validityDays} days.`, rest: ' Code must be used within the validity period.' },
    { icon: '🚫', bold: 'Non-transferable.', rest: ' Codes are issued to the spinning customer and cannot be shared or sold.' },
    { icon: '⚙️', bold: 'Cannot be combined', rest: ' with any other discount, voucher, or promotional offer.' },
    { icon: '⚠️', bold: 'Silux Mobile reserves the right', rest: ' to withdraw this promotion at any time without notice.' },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div id="sw-confetti" className="fixed inset-0 pointer-events-none z-50 overflow-hidden" />
      <style>{`@keyframes swTicker{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>

      {/* Winner ticker */}
      <div className="w-full overflow-hidden bg-gradient-to-r from-primary-600 to-primary-500 py-2.5">
        <div className="whitespace-nowrap text-sm font-semibold text-white/90"
          style={{ display: 'inline-block', animation: 'swTicker 24s linear infinite' }}>
          {tickerText}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{tickerText}
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-10 sm:py-14 px-4 text-center overflow-hidden bg-white border-b border-gray-100">
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-primary-100 rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-primary-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="relative max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-200 mb-5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
            </span>
            <span className="text-sm font-semibold text-primary-700">{campaignName}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-3 leading-tight">
            {heading.split(' ').map((word: string, i: number, arr: string[]) =>
              i === arr.length - 1
                ? <span key={i} className="text-primary-600"> {word}</span>
                : <span key={i}>{word} </span>
            )}
          </h1>
          <p className="text-lg text-gray-500">
            {config?.subheading || 'One spin. Real savings. Exclusively for Silux Mobile customers.'}
          </p>
        </div>
      </section>

      {/* Page body */}
      <div className="max-w-4xl mx-auto px-4 py-10 flex flex-col gap-8">

        {/* Wheel card — centred */}
        <div className="flex justify-center">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-7 sm:p-9 flex flex-col items-center gap-6 w-full max-w-md">

            {/* Validity pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200">
              <span className="text-amber-600 text-sm">⏰</span>
              <span className="text-sm font-bold text-amber-700">Codes valid for {validityDays} days</span>
            </div>

            {/* Wheel */}
            <div className="relative flex items-center justify-center">
              {/* Pointer */}
              <div className="absolute z-10" style={{ top: '-14px', left: '50%', transform: 'translateX(-50%)' }}>
                <div style={{
                  width: 0, height: 0,
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '28px solid #f59e0b',
                  filter: 'drop-shadow(0 2px 6px rgba(245,158,11,0.6))',
                }} />
              </div>

              <canvas ref={canvasRef} style={{
                borderRadius: '50%',
                display: 'block',
                cursor: spinning || isGuestMode ? 'default' : 'pointer',
                boxShadow: '0 0 0 4px rgba(64,163,69,0.15), 0 0 0 8px rgba(64,163,69,0.06), 0 8px 32px rgba(0,0,0,0.1)',
              }} />

              {/* Centre button */}
              <button
                onClick={isGuestMode ? undefined : handleSpin}
                disabled={spinning}
                style={{
                  position: 'absolute', zIndex: 10,
                  width: 66, height: 66, borderRadius: '50%',
                  background: isGuestMode ? '#9ca3af' : 'linear-gradient(135deg,#fbbf24,#f97316)',
                  border: '3px solid #fff',
                  color: '#fff', fontWeight: 900, fontSize: 12,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  cursor: spinning || isGuestMode ? 'default' : 'pointer',
                  boxShadow: isGuestMode ? 'none' : '0 4px 16px rgba(249,115,22,0.5)',
                  fontFamily: 'inherit',
                  transform: spinning ? 'scale(0.95)' : undefined,
                  transition: 'transform .15s',
                }}
              >
                {spinning ? '…' : isGuestMode ? '🔒' : 'SPIN!'}
              </button>
            </div>

            {/* Guest CTA */}
            {isGuestMode && (
              <div className="w-full bg-primary-50 border border-primary-200 rounded-2xl p-5 text-center">
                <p className="text-lg font-bold text-gray-900 mb-1">Login to Spin!</p>
                <p className="text-gray-500 text-sm mb-4">Create a free account or log in to claim your exclusive discount.</p>
                <div className="flex gap-3">
                  <Link to="/register" className="flex-1 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-bold rounded-xl text-sm text-center hover:shadow-lg transition-all hover:-translate-y-0.5">
                    Register Free
                  </Link>
                  <Link to="/login" className="flex-1 py-2.5 bg-white border-2 border-gray-200 hover:border-primary-300 text-gray-700 font-bold rounded-xl text-sm text-center transition-all">
                    Login
                  </Link>
                </div>
              </div>
            )}

            {/* Prize chips */}
            <div className="w-full">
              <p className="text-xs font-bold uppercase tracking-widest text-primary-600 text-center mb-3">Possible Prizes</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {prizes.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
                    {p.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* T&Cs — full width below */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <h3 className="text-lg font-black text-gray-900 mb-5">📋 Terms &amp; Conditions</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
            {tcItems.map((tc, i) => (
              <li key={i} className="flex gap-3 text-sm leading-snug">
                <span className="flex-shrink-0 mt-0.5">{tc.icon}</span>
                <span className="text-gray-500">
                  <strong className="text-gray-800">{tc.bold}</strong>{tc.rest}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-center text-gray-400 leading-relaxed">
          This promotion is offered exclusively by Silux Mobile as part of our {campaignName} celebration.
          Discounts apply at checkout.{' '}
          <Link to="/contact" className="text-primary-600 hover:underline">Contact support</Link> with any questions.
        </p>
      </div>

      {/* Result modal */}
      {showResult && result && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm text-center shadow-2xl border border-gray-100"
            style={{ animation: 'swPop .4s cubic-bezier(.34,1.56,.64,1)' }}>
            <style>{`@keyframes swPop{from{transform:scale(.55) translateY(30px);opacity:0}to{transform:scale(1);opacity:1}}`}</style>

            <div className="text-6xl mb-3">🎉</div>
            <p className="text-sm font-bold uppercase tracking-widest text-primary-600 mb-1">You won!</p>
            <div className="text-5xl sm:text-6xl font-black text-gray-900 mb-1">
              {result.prize_type === 'fixed' ? `£${result.prize_value}` : `${result.prize_value}%`}
            </div>
            <p className="text-gray-500 text-base mb-6">
              {result.prize_type === 'fixed' ? `£${result.prize_value} off` : `${result.prize_value}% off`} your next order!
            </p>

            <div className="bg-primary-50 border border-dashed border-primary-300 rounded-2xl p-4 mb-4">
              <p className="text-primary-600 text-xs font-bold uppercase tracking-widest mb-1">Your Discount Code</p>
              <p className="text-2xl font-black text-gray-900 tracking-widest">{result.discount_code}</p>
              <p className="text-gray-400 text-xs mt-1">Valid for {result.validity_days} days · New orders only</p>
            </div>

            <button onClick={copyCode} className="w-full mb-3 py-2.5 bg-gray-50 border border-gray-200 hover:bg-gray-100 text-sm font-bold text-gray-700 rounded-xl transition-colors">
              {copied ? '✅ Copied!' : '📋 Copy Code'}
            </button>
            <Link to="/shop" className="block w-full py-3 bg-gradient-to-r from-primary-600 to-primary-500 hover:shadow-lg hover:shadow-primary-500/40 text-white font-black text-base rounded-xl transition-all hover:-translate-y-0.5">
              Shop Now &amp; Use My Code →
            </Link>
            <p className="text-gray-400 text-xs mt-4 leading-relaxed">
              New product orders only. Excludes top-ups &amp; account payments. One use per customer.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
